/**
 * FullscreenBong - Full-screen betting slip with blue/gold broadcast theme
 * Supports dual bong display (tipsekspert + guest) side by side
 */

import { TravparkLogo } from './TravparkLogo';

function FullscreenBongColumn({ bongData, bongSelectedLegs, isGreenBong, greenBongLeaving }) {
  if (!bongData) return null;
  const selectedSet = bongSelectedLegs && bongSelectedLegs.length > 0 
    ? new Set(bongSelectedLegs) : null;
  const ownerName = bongData.ownerName || 'Bong';

  return (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Column name header */}
      <div style={{
        fontFamily: "'Barlow Condensed', sans-serif",
        fontSize: '1.6rem', fontWeight: 700, color: 'var(--theme-accent)',
        padding: '14px 32px',
        borderBottom: '1px solid rgba(var(--theme-accent-rgb), 0.2)',
        textTransform: 'uppercase', letterSpacing: '0.04em',
        textAlign: 'center',
      }}>
        {ownerName}
      </div>
      {/* Legs */}
      <div style={{ flex: 1 }}>
        {Object.entries(bongData.legs || {})
          .filter(([leg]) => !selectedSet || selectedSet.has(parseInt(leg)))
          .map(([leg, horses], index) => {
          const hasSplit = horses?.includes('/');
          const horseList = horses?.split(',').map(h => h.trim()).filter(h => h) || [];
          const isBanker = !hasSplit && horseList.length === 1;
          const bankerName = isBanker && bongData.horseNames?.[leg];
          let displayText;
          if (horses?.includes('/')) {
            const [left, right] = horses.split('/');
            const rightHorses = right.split(',').filter(h => h.trim());
            const totalHorseCount = bongData.totalHorsesPerLeg?.[leg] || 0;
            const leftCount = left.split(',').filter(h => h.trim()).length;
            if (totalHorseCount > 0 && (leftCount + rightHorses.length) >= totalHorseCount) {
              displayText = `${left.trim()} / Alle`;
            } else {
              displayText = `${left.trim()} / ${right.trim()}`;
            }
          } else {
            displayText = horses;
          }
          
          return horses && (
            <div
              key={leg}
              className={isGreenBong ? (greenBongLeaving ? 'green-odds-row-exit' : 'green-odds-row-enter') : ''}
              style={{
                display: 'flex', alignItems: 'center', padding: '16px 32px',
                borderBottom: '1px solid var(--theme-panel-border-faint)',
                backgroundColor: index % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'transparent'
              }}
            >
              <div style={{ width: '80px', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: '1.3rem', color: 'var(--theme-accent)' }}>
                {bongData.gameType}-{leg}
              </div>
              <div style={{ flex: 1, textAlign: 'right' }}>
                {isBanker && bankerName ? (
                  <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '1.3rem', fontWeight: 600, color: '#FFFFFF' }}>
                    {displayText}. {bankerName}
                    <span style={{ color: 'var(--theme-accent)', marginLeft: '10px', fontFamily: "'JetBrains Mono', monospace" }}>B</span>
                  </div>
                ) : (
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '1.4rem', fontWeight: 600, color: '#FFFFFF' }}>
                    {displayText}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {/* Cost */}
      {!selectedSet && (
        <div style={{
          padding: '18px 32px', borderTop: '1px solid rgba(var(--theme-accent-rgb), 0.2)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1.2rem', fontWeight: 700, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase' }}>
            Kostnad
          </div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '1.5rem', fontWeight: 700, color: 'var(--theme-accent)' }}>
            {bongData.hasSplit ? `${bongData.cost?.toFixed(2)} / ${bongData.costLarge?.toFixed(2)} kr` : `${bongData.cost?.toFixed(2)} kr`}
          </div>
        </div>
      )}
    </div>
  );
}

export function FullscreenBong({
  bongData, bongDataGuest, trackName, useWebcam, webcamDeviceId, audioDeviceId,
  isGreenBong, greenBongLeaving, bongSelectedLegs, isVmix, sommertravetMode
}) {
  if (!bongData) return null;
  const hasBoth = bongData && bongDataGuest;

  return (
    <div 
      data-testid="fullscreen-view"
      style={{ 
        backgroundImage: (useWebcam || isGreenBong) ? 'none' : `url(https://customer-assets.emergentagent.com/job_37d71e0f-ea96-4348-a27f-5a06f0067365/artifacts/ywps6pb9_file_00000000df9c7243b3636a21f3a725db.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: isGreenBong ? (isVmix ? 'transparent' : (useWebcam ? 'transparent' : '#00FF00')) : (useWebcam ? 'transparent' : '#050F23'),
        height: '100vh', 
        width: '100vw', 
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isGreenBong ? '40px 120px 80px' : '90px 90px 80px',
        boxSizing: 'border-box',
        position: 'relative'
      }}
    >
      {/* Main panel */}
      <div
        className={isGreenBong ? (greenBongLeaving ? 'green-odds-box-exit' : 'green-odds-box-enter') : ''}
        style={{
          borderRadius: '20px',
          border: '3px solid rgba(var(--theme-accent-rgb), 0.5)',
          backgroundColor: '#050F23',
          boxShadow: isGreenBong ? 'none' : '0 0 50px rgba(var(--theme-accent-rgb), 0.2), 0 25px 50px rgba(0,0,0,0.5)',
          width: hasBoth ? '80%' : (isGreenBong ? '50%' : '60%'),
          maxWidth: hasBoth ? '1100px' : '800px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1,
          position: 'relative'
        }}
      >
        {/* Header */}
        <div 
          className={isGreenBong ? (greenBongLeaving ? 'green-odds-header-exit' : 'green-odds-header-enter') : ''}
          style={{
            background: 'linear-gradient(135deg, var(--theme-header-gradient) 0%, transparent 100%)',
            borderBottom: '3px solid rgba(var(--theme-accent-rgb), 0.5)',
            color: '#FFFFFF', padding: '28px 32px',
            display: 'flex', alignItems: 'center',
          }}
        >
          <TravparkLogo height={100} sommertravetMode={sommertravetMode} style={{ position: 'absolute', left: '32px' }} />
          <div style={{ textAlign: 'center', width: '100%' }}>
            <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '3rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--theme-accent)' }}>
              {bongData.gameType} BONG
            </div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '1.25rem', color: 'rgba(255,255,255,0.6)', marginTop: '8px', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              {bongData.trackName || trackName}
            </div>
          </div>
        </div>

        {/* Bong columns */}
        <div style={{ display: 'flex', flex: 1 }}>
          <FullscreenBongColumn bongData={bongData} bongSelectedLegs={bongSelectedLegs} isGreenBong={isGreenBong} greenBongLeaving={greenBongLeaving} />
          {hasBoth && (
            <>
              <div style={{ width: '3px', backgroundColor: 'var(--theme-gradient-line-subtle)', flexShrink: 0 }} />
              <FullscreenBongColumn bongData={bongDataGuest} bongSelectedLegs={bongSelectedLegs} isGreenBong={isGreenBong} greenBongLeaving={greenBongLeaving} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
